import { ChangeEvent, useState } from 'react';
import {FormikProvider, useFormik } from 'formik';
import Modal from '../../BaseComponents/Modal';
import TextInput from '../../BaseComponents/TextInput';
import Button from '../../BaseComponents/Button';
import './ClinicModal.scss';
import TextArea from '../../BaseComponents/TextArea';
import {
  createClinic,
  getClinicsList,
  updateClinic,
} from '../../../api/clinics';
import { useDispatch, useSelector } from 'react-redux';
import { setClinicsCount, setClinicsList } from '../../../store/clinicsSlice';
import { RootState } from '../../../store/store';
import { emailRegexp } from '../../../utils/constants';
import { CustomInput } from '../../location/CustomInput';
import PhoneInputComp from '../../BaseComponents/PhoneInput';
import Select, { SelectOption } from '../../BaseComponents/Select';
import UploadPhoto from '../../UploadPhoto';
type TypeName = {
  isOpen: boolean;
  onClose: () => void;
};

interface FormValues {
  name: string;
  location: string;
  description: string;
  email: string;
  number: string;
  locationURL: string;
  profile_image:string;
  clinic_type:string;
  gs_id:string;
}

const clinicTypesOptions : SelectOption[]=[
  {key:'pharmacy',value:'Pharmacy'},
  {key:'clinic',value:'Clinic'},
  {key:'scan_centre',value:'Scan Centre'},
]

const AddClinicModal: React.FC<TypeName> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFormError, setIsFormError] = useState<boolean>(false);
  const { clinicDataToEdit, clinicsList, clinicsCount } = useSelector(
    (state: RootState) => state.clinics
  );

  const validate = (values: FormValues) => {
    const errors: any = {};

    // Name
    if (!values.name) {
      errors.name = 'Name is required';
    } else if (values.name.length < 2) {
      errors.name = 'Name is too short';
    }
    // Location
    if (!values.location) {
      errors.location = 'Location is required';
    }
  
    // Email
    if (values.email && !emailRegexp.test(values.email)) {
      
      errors.email = 'Invalid email address';
    }
    // Number
    if (!values.number) {
      errors.number = 'Number is required';
    } 
    return errors;
  };

  const handleFieldChange = <T extends keyof FormValues>(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: T
  ) => {
    if (formik.errors[name] && formik.submitCount > 0) {
      formik.setFieldError(name, '');
    }
    formik.handleChange(e);
  };

  const initialValues: FormValues = clinicDataToEdit
    ? {
        name: clinicDataToEdit.title,
        email: clinicDataToEdit.email ||"",
        description: clinicDataToEdit.description,
        location: clinicDataToEdit.address,
        number: clinicDataToEdit.phone,
        locationURL: clinicDataToEdit.maps_link ||"",
        profile_image:clinicDataToEdit.image ||"",
        clinic_type:clinicDataToEdit.clinic_type ||"",
        gs_id:clinicDataToEdit.gs_id ||"",
      }
    : {
        name: '',
        email: '',
        description: '',
        location: '',
        number: '',
        locationURL: '',
        profile_image:'',
        clinic_type:'',
        gs_id:'', 
      };

  const formik = useFormik<FormValues>({
    initialValues,
    validate,
    onSubmit: async (values, { resetForm }) => {
      if (isFormError) setIsFormError(false);
      setIsLoading(true);
      try {
        const newClinicData = {
          title: values.name,
          address: values.location,
          phone:   values.number[0] !== '+' ? '+' + values.number : values.number,
          email: values.email || null,
          description: values.description,
          maps_link: values.locationURL || null,
          image:values.profile_image||null,
          clinic_type:values.clinic_type||null,
          gs_id:values.gs_id||null,
        };

        clinicDataToEdit
          ? await updateClinic({
              ...newClinicData,
              clinic_id: clinicDataToEdit.id!,
            })
          : await createClinic(newClinicData);

        if (clinicDataToEdit && clinicsList) {
          const updatedClinic: any = {
            ...clinicDataToEdit,
            ...newClinicData,
            title: newClinicData.title,
            address: newClinicData.address,
            phone: newClinicData.phone,
            email: newClinicData.email || '',
            description: newClinicData.description,
            maps_link: newClinicData.maps_link,
            image: newClinicData.image,
            clinic_type: newClinicData.clinic_type,
            gs_id: newClinicData.gs_id
          };
          dispatch(
            setClinicsList(
              clinicsList.map((clinic) =>
                clinic.id === clinicDataToEdit.id
                  ? updatedClinic
                  : clinic
              )
            )
          );
        } else {
          const clinicsList = await getClinicsList(0, 1000);
          dispatch(setClinicsList(clinicsList.clinics));
          dispatch(setClinicsCount(clinicsCount ? clinicsCount + 1 : 0));
        }

        onClose();
        resetForm();
      } catch (error) {
        console.error('Error creating clinic:', error);
        setIsFormError(true);
      } finally {
        setIsLoading(false);
      }
    },
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
  });

  const isSubmitDisabled =
    isLoading ||
    !formik.values.name.trim() ||
    !formik.values.location.trim() ||
    !formik.values.number.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={'Add Clinic'}
      width={'520px'}
    ><FormikProvider value={formik}>
      <form
        onSubmit={formik.handleSubmit}
        noValidate
        className='clinic-modal-container'
      >
        <div className='main-info-container'>
          <UploadPhoto
          onPhotoChange={(url)=>formik.setFieldValue('profile_image',url)}
          defaultPhoto={formik.values.profile_image}
          putToStaticFolder={true} 
          />
          <div className='section-title'>Main Info</div>
          <div className='inputs-wrapper'>
            <TextInput
              name='name'
              placeholder='Enter name *'
              type='text'
              isError={formik.submitCount > 0 && !!formik.errors.name}
              errorMessage={
                formik.submitCount > 0 ? formik.errors.name : undefined
              }
              onChange={handleFieldChange}
              value={formik.values.name}
            />
            <div className='address-search-wrapper'>
              <CustomInput
                value={formik.values.location}
                onChange={(value: string) => {
                  formik.setFieldValue('location', value);
                }}
              />
            </div>
            <TextInput
              name='locationURL'
              placeholder='Enter Google Maps URL '
              type='text'
              isError={formik.submitCount > 0 && !!formik.errors.locationURL}
              errorMessage={
                formik.submitCount > 0 ? formik.errors.locationURL : undefined
              }
              onChange={handleFieldChange}
              value={formik.values.locationURL}
            />
            <Select
                name="clinic_type"
                placeholder="Select Type"
                options={clinicTypesOptions}
                selectedOption={
                  clinicTypesOptions.find(opt => opt.value === formik.values.clinic_type) ||
                  (formik.values.clinic_type ? { key: formik.values.clinic_type.toLowerCase(), value: formik.values.clinic_type } : null)
                }
                onChange={(option) => formik.setFieldValue('clinic_type', option.value)}
                isError={false}
              />
              <TextInput
                name='gs_id'
                placeholder='Enter GS ID'
                type='text'
                onChange={handleFieldChange}
                value={formik.values.gs_id}
                isError={false}
              />

            <TextArea
              name='description'
              placeholder='Enter description'
              isError={formik.submitCount > 0 && !!formik.errors.description}
              errorMessage={
                formik.submitCount > 0 ? formik.errors.description : undefined
              }
              onChange={handleFieldChange}
              value={formik.values.description}
              rows={4}
            />
          </div>
        </div>
        <div className='contact-info-container'>
          <div className='section-title'>Contact info</div>
          <div className='inputs-wrapper'>
            <TextInput
              name='email'
              placeholder='Enter email '
              type='email'
              isError={formik.submitCount > 0 && !!formik.errors.email}
              errorMessage={
                formik.submitCount > 0 ? formik.errors.email : undefined
              }
              onChange={handleFieldChange}
              value={formik.values.email}
            />
            <PhoneInputComp
              placeholder='Enter number *'
              selectedPhone={formik.values.number}
              onPhoneChange={(phone) => formik.setFieldValue('number', phone)}
            />
          </div>
        </div>
        <div className='btn-wrapper'>
          <Button
            text={clinicDataToEdit ? 'Update clinic' : 'Add Clinic'}
            type='submit'
            isLoading={isLoading}
            isDisabled={isSubmitDisabled}
          />
          {isFormError && (
            <div className='error-message'>
              Something went wrong. Please try again.
            </div>
          )}
        </div>
      </form>
      </FormikProvider>
    </Modal>
  );
};

export default AddClinicModal;
